<template>
  <div class="drawer-content-student">
    <div v-if="loading" class="loading-state">
      <IonSpinner name="crescent" size="small" />
    </div>

    <div v-else class="categories-scroll">
      <CategoriesList
        v-if="modulesWithLinks.length > 0"
        :categories="modulesWithLinks"
        :is-open="true"
        @link-click="handleLinkClick"
      />
    </div>

    <div v-if="shouldShowTools" class="drawer-tools-section">
      <button class="tools-toggle" @click="toggleTools">
        <span class="tools-toggle-label">{{ t('common.tools.title') }}</span>
        <IonIcon
          :icon="isToolsExpanded ? chevronDownOutline : chevronUpOutline"
          class="tools-toggle-icon"
        />
      </button>
      <Transition name="expand">
        <ToolsGrid
          v-if="isToolsExpanded"
          size="compact"
          :columns="4"
          @tool-click="handleLinkClick"
        />
      </Transition>
    </div>

    <div class="drawer-footer">
      <UserRoutesGrid
        :user-routes="displayRoutes"
        :is-expanded="true"
        @route-click="handleLinkClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { IonSpinner, IonIcon } from '@ionic/vue';
import { chevronUpOutline, chevronDownOutline } from 'ionicons/icons';
import { useModuleApi } from '@/features/menu-management/composables/useModuleApi';
import { useUserRoutes } from '@/common/composables/useUserRoutes';
import { useLayout } from '@/layouts/composables/useLayout';
import { useLocale } from '@/common/composables/useLocale';
import CategoriesList from '@/layouts/components/category-collapse/CategoriesList.vue';
import UserRoutesGrid from '@/layouts/components/UserRoutesGrid.vue';
import ToolsGrid from '@/layouts/components/ToolsGrid.vue';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useLocale();

const { modulesWithLinks, loading } = useModuleApi();
const { displayRoutes } = useUserRoutes();
const { shouldShowTools } = useLayout();

const isToolsExpanded = ref(false);

function toggleTools() {
  isToolsExpanded.value = !isToolsExpanded.value;
}

function handleLinkClick() {
  emit('close');
}
</script>

<style scoped>
.drawer-content-student {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.categories-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  scrollbar-width: thin;
  scrollbar-color: var(--ion-color-medium) transparent;
}

.categories-scroll::-webkit-scrollbar {
  width: 4px;
}

.categories-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.categories-scroll::-webkit-scrollbar-thumb {
  background-color: var(--ion-color-medium);
  border-radius: 2px;
}

.drawer-tools-section {
  flex-shrink: 0;
  padding: 0 16px 12px;
  border-top: 1px solid var(--ion-border-color);
  background: var(--ion-background-color);
}

.tools-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--ion-text-color);
}

.tools-toggle-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ion-color-medium);
}

.tools-toggle-icon {
  font-size: 18px;
  color: var(--ion-color-medium);
  transition: transform 0.2s ease;
}

.drawer-footer {
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid var(--ion-border-color);
  background: var(--ion-background-color);
  box-shadow: var(--ion-box-shadow);
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  flex: 1;
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 200px;
}
</style>
